import React, { useMemo, useRef, useState } from 'react';
import useSize from '@react-hook/size';
import { metIntegration } from '@metservicedev/platforms-services-interface';
import type { WeatherWidgetRenderProps } from '@mono/types/src/weather-widgets';
import { InfoPopover } from '@mono/metui/src/info-popover';
import { IssueTime } from '@mono/metui';
import { WindBarsSettings } from '@mono/types/src/weather-widgets/wind-bars-settings';
import { toHash } from '@mono/to-hash';
import { usePlatStageContext } from '@mono/use-plat-stage';
import { WindBar } from 'wind-bar';
import { convertValueUnit } from '@metservicedev/cf-units';
import type { IWindBarData } from 'wind-bars.types';
import { Hr, HeaderCell, TableCell } from 'wind-bars.styles';

const METJWT = localStorage.getItem('metjwt');
const HEADERS: {} | { authorization: string } = METJWT
    ? {
          authorization: `Bearer ${METJWT}`,
      }
    : {};
export const WindBars = ({ datasets, isLoading, theme: themeName, timezone, settings }: WeatherWidgetRenderProps<WindBarsSettings>) => {
    const widgetContainerRef = useRef<HTMLDivElement>(null);

    const isDarkMode = themeName === 'dark';
    const theme = isDarkMode
        ? {
              text: '#ffffff',
              issueTime: '#ffffff',
              arrowTextColor: '#172344',
          }
        : {
              text: '#172344',
              issueTime: '#595959',
              arrowTextColor: '#ffffff',
          };

    const { stage } = usePlatStageContext();

    const { metserviceApi } = metIntegration;

    const [data, setData] = useState<Record<string, IWindBarData>>();

    const [width, height] = useSize(widgetContainerRef);

    useMemo(() => {
        let timeoutHook = 0;
        const abortController = new AbortController();

        const patientQuery = async () => {
            try {
                await Promise.all(
                    settings.bars.map(async (bar) => {
                        const { id, datasetId } = bar;
                        if (!datasetId) {
                            return;
                        }
                        const res = await fetch(`${metserviceApi[stage]}/observations/nz/1-minute/weatherStation/${datasetId?.split('.')[1]}/latest`, {
                            headers: HEADERS,
                            signal: abortController.signal,
                        });

                        const value = await res.json();
                        const { obs_timestamp, windlul_10mnmin, windgst_10mnmax, windspd_10mnavg, windspd_01mnavg } = value.results[0] as IWindBarData;

                        setData((prev) => ({
                            ...prev,
                            [id]: {
                                obs_timestamp,
                                windlul_10mnmin,
                                windgst_10mnmax,
                                windspd_10mnavg,
                                windspd_01mnavg,
                            },
                        }));
                    })
                );
            } catch (e) {
                console.warn(e);
                if (abortController.signal.aborted === true) return;
                setData((prev) => prev);
            } finally {
                timeoutHook = setTimeout(patientQuery, 55 * 1000) as unknown as number;
            }
        };
        patientQuery();
        return () => {
            abortController.abort();
            clearTimeout(timeoutHook);
        };
    }, [stage, metserviceApi, toHash(settings.bars)]);

    const barData = useMemo(() => {
        const d = settings.bars.map((bar, index) => {
            if (!bar.datasetId) {
                console.warn(`no datasetId found for bar: ${bar.id}`);
                return;
            }

            const dataset = datasets?.find((d) => d.id === bar.datasetId);

            if (!data || !data[bar.id]) {
                console.warn(`no data found for bar: ${bar.id}`);
                return;
            }
            const { obs_timestamp, windlul_10mnmin, windgst_10mnmax, windspd_10mnavg, windspd_01mnavg } = data[bar.id];

            const commonProps = {
                from: 'knots',
                to: bar.unit,
                decimalPrecision: 0,
            };
            const minLul10min = convertValueUnit({
                ...commonProps,
                value: windlul_10mnmin,
            }) as number;
            const maxGust10min = convertValueUnit({
                ...commonProps,
                value: windgst_10mnmax,
            }) as number;
            const windSpeedAverage10m = convertValueUnit({
                ...commonProps,
                value: windspd_10mnavg,
            }) as number;

            const windSpeedAverage1m = convertValueUnit({
                ...commonProps,
                value: windspd_01mnavg,
            }) as number;
            return {
                ...bar,
                obs_timestamp,
                minLul10min,
                maxGust10min,
                windSpeedAverage10m,
                windSpeedAverage1m,
                dataset,
            };
        });
        return d;
    }, [toHash(data), toHash(datasets), toHash(settings.bars)]);

    const targetTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    const issueTime = useMemo(() => {
        if (!data) return undefined;
        return Object.values(data)
            .map(({ obs_timestamp }) => obs_timestamp)
            .filter(Boolean)
            .sort()
            .reverse()[0];
    }, [toHash(data)]);

    const mode = settings.mode ?? 'cozy';

    const multipleBars = barData?.length > 1;

    const estimatedTableCellWidth = mode === 'cozy' ? 130 : 100;
    const estimatedTableHeight = 258;
    const windSpeedWidth = (width - (multipleBars ? estimatedTableCellWidth : 0)) / (barData?.length ? barData.length : 1);
    const windSpeedHeight = height - estimatedTableHeight;

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                minWidth: barData?.length * 110 + (multipleBars ? (mode === 'cozy' ? 130 : 100) : 0),
                minHeight: 512,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                font: '400 12px/18px Open Sans',
                color: theme.text,
            }}
            ref={widgetContainerRef}
        >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {/* Title */}
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ fontSize: 16, lineHeight: '22px', fontWeight: 700 }}>Wind speed</div>
                </div>
                {/* Infopopeover */}
                <div style={{ display: 'flex', columnGap: 4 }}>
                    <IssueTime
                        issueTime={issueTime}
                        targetTimezone={targetTimezone}
                        isDarkMode={isDarkMode}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            color: theme.issueTime,
                        }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <InfoPopover popoverProps={{ placement: 'rightTop' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 420, padding: 16 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <div>
                                        <strong>Arrow: </strong>Wind speed average for the last 1 minute.
                                    </div>
                                    <div>
                                        <strong>Horizontal line: </strong>Wind speed average for the last 10 minutes.
                                    </div>
                                    <div>
                                        <strong>Coloured bar: </strong>Maximum wind speed (gust) and minimum wind speed (lull) for the last 10 minutes. The
                                        colour of the bars corresponds to the maximum gust threshold set for them.
                                    </div>
                                </div>
                            </div>
                        </InfoPopover>
                    </div>
                </div>
            </div>

            {/* Divider */}
            <hr style={{ width: '100%', border: 'none', borderBottom: '1px solid #cdcdcd' }} />

            {/* Content */}
            <>
                {/* BarContainer */}
                <div style={{ display: 'flex', paddingTop: 16 }}>
                    {multipleBars && <div style={{ width: mode === 'cozy' ? 130 : 100 }} />}
                    {barData?.map((d, index) => {
                        if (!d) return;
                        return (
                            <WindBar
                                key={index}
                                {...{ ...d, height: windSpeedHeight, width: windSpeedWidth, color: theme.text, arrowTextColor: theme.arrowTextColor }}
                            />
                        );
                    })}
                </div>

                {/* Table */}
                <div style={{ display: 'flex' }}>
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', position: 'absolute' }}>
                        <Hr style={{ marginTop: '40px' }} />
                        <Hr style={{ marginTop: '30px' }} />
                        <Hr style={{ marginTop: '36px' }} />
                        <Hr style={{ marginTop: '34px' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <HeaderCell mode={mode}>LOCATION</HeaderCell>
                        <HeaderCell mode={mode}>1 MIN AVG</HeaderCell>
                        <HeaderCell mode={mode}>10 MIN AVG</HeaderCell>
                        <HeaderCell mode={mode}>10 MIN GUST</HeaderCell>
                        <HeaderCell mode={mode}>10 MIN LULL</HeaderCell>
                    </div>

                    {/* Actual Table Data */}
                    {barData?.map((d, index) => {
                        const location = d?.location ? d.location : (d?.dataset?.display_name_short ?? 'N/A');
                        return (
                            <div
                                key={`Table-${index}-${d?.id}`}
                                style={{ display: 'flex', flexDirection: 'column', flex: 12, fontSize: 14, textAlign: multipleBars ? 'center' : 'right' }}
                            >
                                {/* Dataset name */}
                                <TableCell mode={mode}>{location}</TableCell>
                                {/* One min avg */}
                                <TableCell mode={mode}>{d?.windSpeedAverage1m ? `${d.windSpeedAverage1m} ${d.unitLabel}` : 'N/A'}</TableCell>
                                {/* Ten min avg */}
                                <TableCell mode={mode}>{d?.windSpeedAverage10m ? `${d.windSpeedAverage10m} ${d.unitLabel}` : 'N/A'}</TableCell>
                                {/* Ten min gust */}
                                <TableCell mode={mode}>{d?.maxGust10min ? `${d.maxGust10min} ${d.unitLabel}` : 'N/A'}</TableCell>
                                {/* Ten min lul */}
                                <TableCell mode={mode}>{d?.minLul10min ? `${d.minLul10min} ${d.unitLabel}` : 'N/A'}</TableCell>
                            </div>
                        );
                    })}
                </div>
            </>
        </div>
    );
};
